import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDownload } from "../pages/DownloadWindow/DownloadContext";
import { trackDownloadClick } from "../lib/analytics";

export const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=ai.zerotyping";

export const APP_STORE_URL =
  "https://apps.apple.com/us/app/zero-typing-ai-voice-to-text/id6752406283";

// Download button background. The first color is held to a narrow strip on the
// left (roughly the icon area) before transitioning to the second color, so the
// button reads mostly as #407BFF with a lighter #3AB1F6 accent under the icon.
export const DOWNLOAD_GRADIENT =
  "linear-gradient(90deg, #3AB1F6 0%, #407BFF 50px)";

// Same installer the footer's "Download for Windows" button points at.
export const WINDOWS_INSTALLER_URL =
  process.env.REACT_APP_INSTALLER_URL ||
  "https://zerotyping.ai/ztaupdate/ZeroTypingAI.msi";

// Triggers the browser's native download of the .msi, exactly like the footer's
// <a href> anchor does (starts the real download; not the streaming fetch).
const triggerWindowsDownload = () => {
  const a = document.createElement("a");
  a.href = WINDOWS_INSTALLER_URL;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

/**
 * Detects the visitor's operating system from the user agent.
 * Returns one of: "windows" | "android" | "ios" | "mac" | "unknown".
 */
export const detectOS = () => {
  if (typeof navigator === "undefined") return "unknown";
  const ua = (navigator.userAgent || "").toLowerCase();
  const platform = (navigator.platform || "").toLowerCase();

  // Android must be checked before "linux"/"mac" since some UAs include those.
  if (ua.includes("android")) return "android";

  // iOS: iPhone/iPad/iPod. iPadOS 13+ masquerades as Mac, so also detect a
  // touch-capable "MacIntel" as iOS.
  if (
    /iphone|ipad|ipod/.test(ua) ||
    (platform === "macintel" && navigator.maxTouchPoints > 1)
  ) {
    return "ios";
  }

  if (ua.includes("win")) return "windows";
  if (ua.includes("mac")) return "mac";

  return "unknown";
};

// The macOS app ships through the Mac App Store, so every Mac CTA opens its
// listing rather than pulling an installer down directly.
export const MAC_APP_STORE_URL =
  "https://apps.apple.com/pk/app/zero-typing-ai-voice-to-text/id6752406283";

// Label shared by every Mac-facing download button.
export const MAC_DOWNLOAD_LABEL = "Download for Mac";

/**
 * OS-aware label for a download button, e.g. "Download for Windows".
 * Falls back to "Download App" when the OS can't be determined.
 */
export const getDownloadLabel = () => {
  switch (detectOS()) {
    case "windows":
      return "Download for Windows";
    case "mac":
      return MAC_DOWNLOAD_LABEL;
    case "ios":
      return "Download for iPhone";
    case "android":
      return "Download for Android";
    default:
      return "Download App";
  }
};

/**
 * Returns a click handler for "Download App" buttons that adapts to the
 * visitor's OS instead of always routing to the /download page:
 *   - windows  -> start the .msi download + show the Windows install modal
 *                 (identical to the footer's "Download for Windows" button)
 *   - android  -> open the Google Play listing in a new tab
 *   - mac/ios  -> open the App Store listing in a new tab
 *   - unknown  -> route to the /download page (safe fallback)
 *
 * `location` is an optional label forwarded to analytics (e.g. "home_hero").
 */
export const useSmartDownload = () => {
  const download = useDownload();
  const navigate = useNavigate();

  return useCallback(
    (location = "unknown") => {
      const os = detectOS();

      switch (os) {
        case "windows":
          trackDownloadClick({
            platform: "windows",
            file_name: "ZeroTypingAI.msi",
            location,
          });
          // Stream the installer through DownloadContext so progress is shown
          // and `file_download` fires when the file is actually on disk (a
          // native <a href> download is invisible to JS). It opens the install
          // modal itself. Outside the provider, fall back to the native download.
          if (download?.startDownload) {
            download.startDownload({ platform: "windows", location });
          } else {
            triggerWindowsDownload();
          }
          break;

        case "android":
          trackDownloadClick({ platform: "android", location });
          window.open(PLAY_STORE_URL, "_blank", "noopener,noreferrer");
          break;

        case "ios":
          trackDownloadClick({ platform: "ios", location });
          window.open(APP_STORE_URL, "_blank", "noopener,noreferrer");
          break;

        case "mac":
          trackDownloadClick({ platform: "mac", location });
          window.open(MAC_APP_STORE_URL, "_blank", "noopener,noreferrer");
          break;

        default:
          trackDownloadClick({ platform: os, location });
          navigate("/download");
          break;
      }
    },
    [download, navigate]
  );
};
